/**
 * Telemetry flush — batched POST в backend `/events`.
 *
 * Архитектура:
 *   1. `emit()` пишет в локальный ring buffer + добавляет в `pending` queue
 *   2. setInterval каждые FLUSH_INTERVAL_MS вызывает `flushNow()`
 *   3. `flushNow()` достаёт до BATCH_LIMIT событий, шлёт POST /events
 *   4. На visibilitychange→hidden + beforeunload — синхронный flush с
 *      `keepalive: true` чтобы успел уйти при закрытии вкладки
 *
 * Gates (skip flush если что-либо из):
 *   - VITE_API_BASE env var не задан (dev без backend'а)
 *   - consent не 'granted' (GDPR)
 *   - pending queue пустой
 *   - in-flight request уже идёт (защита от race)
 *
 * Backend endpoint спецификация — см. `apps/api/src/index.ts` POST /events.
 */
import { canSendTelemetry } from './consent.js';
import { getAnonId } from './anonId.js';
import { isCapacitorRuntime } from '../platform/android.js';
import { isTelegramRuntime } from '../platform/telegram.js';

const FLUSH_INTERVAL_MS = 30_000;
/** Backend принимает до 200 событий за batch (apps/api validates тоже). */
const BATCH_LIMIT = 200;

interface PendingEvent {
  readonly name: string;
  readonly category: string;
  readonly payload: unknown;
  readonly occurred_at_ms: number;
}

const pending: PendingEvent[] = [];
let inFlight = false;
let scheduler: number | null = null;

function getApiBase(): string | null {
  const base = import.meta.env.VITE_API_BASE ?? '';
  if (base.length === 0) return null;
  // Снимаем trailing slash чтобы `${base}/events` всегда был корректный URL.
  return base.replace(/\/$/, '');
}

function detectPlatform(): 'web' | 'telegram' | 'android' {
  if (isCapacitorRuntime()) return 'android';
  if (isTelegramRuntime()) return 'telegram';
  return 'web';
}

function detectLocale(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  return navigator.language;
}

/**
 * Max events удерживаемых в pending очереди при затяжном outage.
 * BATCH_LIMIT × 5 = до 5 батчей retry. Дольше — старейшие drop'ятся.
 * Без cap'а очередь могла расти бесконечно (memory leak, slow JSON.stringify).
 */
const MAX_PENDING = 1000;

/** Добавить событие в очередь. Если очередь набрала BATCH_LIMIT — flush сразу. */
export function enqueueEventForFlush(e: PendingEvent): void {
  pending.push(e);
  // 2026-05-26 BUG FIX: cap общей очереди при затяжном outage.
  // Если backend down >5 минут, retry queue могла разрастаться до
  // тысяч events. Drop старейшие (FIFO trim) когда превышен лимит.
  if (pending.length > MAX_PENDING) {
    pending.splice(0, pending.length - MAX_PENDING);
  }
  if (pending.length >= BATCH_LIMIT) {
    void flushNow();
  }
}

/**
 * Послать pending события на backend. Идемпотентно — если уже идёт
 * запрос, новый не стартует.
 *
 * Возвращает Promise<void>. Не throws — все ошибки логируются и события
 * дропаются (analytics не критичны для gameplay).
 *
 * `keepalive` опция fetch'а позволяет запросу завершиться даже после
 * navigation/page unload — идеально для visibilitychange→hidden flush.
 */
export async function flushNow(): Promise<void> {
  if (inFlight) return;
  const base = getApiBase();
  if (base === null) return;
  if (!canSendTelemetry()) return;
  if (pending.length === 0) return;

  // Достаём batch синхронно чтобы новые события могли пушиться пока
  // запрос летит.
  const batch = pending.splice(0, BATCH_LIMIT);
  const anonId = getAnonId();
  const platform = detectPlatform();
  const locale = detectLocale();
  // __APP_VERSION__ — compile-time const из vite.config.ts (git short SHA
  // или 'unknown' fallback). Всегда string, не nullable.
  const appVersion = __APP_VERSION__;

  const body = JSON.stringify({
    app_version: appVersion,
    events: batch.map((e) => ({
      name: e.name,
      category: e.category,
      payload: e.payload,
      occurred_at_ms: e.occurred_at_ms,
      anon_id: anonId,
      platform,
      locale,
    })),
  });

  inFlight = true;
  try {
    const res = await fetch(`${base}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      // keepalive позволяет request'у завершиться после page unload.
      // Browser cap'ает keepalive body на 64KB — наш batch до 200 событий
      // ×~200 bytes ≈ 40KB, влезаем.
      keepalive: true,
    });
    if (!res.ok) {
      console.warn('[telemetry] flush failed:', res.status);
      // 2026-05-26 BUG FIX: re-add batch на retry queue вместо drop.
      // Раньше events дропались навсегда при 5xx/network error —
      // funnel firsts терялись после короткого backend outage.
      // 4xx (client error) всё ещё drop — batch malformed и retry
      // не поможет; 5xx и network — retry на следующем flush tick.
      if (res.status >= 500) {
        pending.unshift(...batch);
      }
    }
  } catch (err) {
    // Network error / CORS / timeout — re-add для retry. На следующий
    // flush tick попробуем снова. Cap общего pending'а ограничен
    // BATCH_LIMIT × 5 = 1000 events — если backend down дольше, drop
    // старейшие (FIFO trim в emit()).
    console.warn('[telemetry] flush error:', err);
    pending.unshift(...batch);
  } finally {
    inFlight = false;
  }
}

/**
 * Запустить периодический flush. Возвращает cleanup function (вызывать на
 * unmount). Вызывается из <FlowTrackers />.
 */
export function startFlushScheduler(): () => void {
  if (scheduler !== null) {
    // Уже запущен — no-op, возвращаем reusable cleanup.
    return () => {
      if (scheduler !== null) {
        window.clearInterval(scheduler);
        scheduler = null;
      }
    };
  }
  scheduler = window.setInterval(() => {
    void flushNow();
  }, FLUSH_INTERVAL_MS);
  return () => {
    if (scheduler !== null) {
      window.clearInterval(scheduler);
      scheduler = null;
    }
  };
}

/** Test-only: статистика очереди для отладки. */
export function getFlushQueueSize(): number {
  return pending.length;
}
